// src/pages/Tabordenes.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, ResponsiveContainer,
} from "recharts";

// SERIES visibles en el gráfico (orden y color)
const SERIES = [
  { key: "Entregado", color: "#22c55e" },               // Verde
  { key: "Rechazado por cliente", color: "#ef4444" },   // Rojo
  { key: "Novedad", color: "#eab308" },                 // Amarillo
  { key: "Preparando", color: "#a855f7" },              // Morado claro
  { key: "Recolectado", color: "#06b6d4" },             // Celeste
  { key: "En Camino", color: "#1e90ff" },               // Azul eléctrico
  { key: "Guia", color: "#7e22ce" },                    // Morado oscuro
  { key: "Pendiente", color: "#9ca3af" },               // Gris
];

// Sinónimos por si el backend varía mayúsculas/acentos/underscores
const ALIASES = {
  "Entregado": ["Entregado"],
  "Rechazado por cliente": ["Rechazado por cliente", "Rechazado por Cliente"],
  "Novedad": ["Novedad"],
  "Preparando": ["Preparando"],
  "Recolectado": ["Recolectado"],
  "En Camino": ["En Camino", "En camino", "En_camino", "EnCamino"],
  "Guia": ["Guia", "Guía"], // tolera tilde
  "Pendiente": ["Pendiente"],
};

const sortMonth = (a, b) => {
  const [ma, ya] = a.split("-").map(Number);
  const [mb, yb] = b.split("-").map(Number);
  return new Date(ya, ma - 1, 1) - new Date(yb, mb - 1, 1);
};

// Tooltip oscuro
const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  const items = payload.filter((p) => (p.value ?? 0) > 0);
  if (!items.length) return null;
  return (
    <div style={{
      background:"#111827", border:"1px solid #374151",
      borderRadius:8, padding:"8px 10px", color:"#F3F4F6"
    }}>
      <div style={{fontWeight:700, marginBottom:6}}>{label}</div>
      {items.map((p) => (
        <div key={p.dataKey} style={{display:"flex",gap:8,alignItems:"center"}}>
          <span style={{width:10,height:10,background:p.color,borderRadius:2}} />
          <span>{p.dataKey}:</span>
          <span style={{marginLeft:"auto",fontWeight:700}}>{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const Tabordenes = ({ lastUpdate }) => {
  const [data, setData] = useState([]);

  const fetchData = async () => {
    const ts = lastUpdate || Date.now(); // cache-busting
    const res = await fetch(
      `https://marketbusters.pro/Dropdrop/pedidos_cluster_mensual.json?t=${ts}`,
      { cache: "no-store" }
    );
    const json = await res.json();
    const grouped = json.grouped || {};

    const months = Object.keys(grouped).sort(sortMonth);
    const rows = months.map((m) => {
      const src = grouped[m] || {};
      const row = { mes: m };

      // Para cada serie, suma los posibles sinónimos presentes en el JSON
      SERIES.forEach((s) => {
        const aliases = ALIASES[s.key] || [s.key];
        let val = 0;
        for (const k of aliases) {
          const v = src[k];
          if (typeof v === "number") val += v;
        }
        row[s.key] = val;
      });

      return row;
    });

    // Solo meses con al menos una barra > 0
    const filtered = rows.filter((r) =>
      SERIES.some((s) => (r[s.key] ?? 0) > 0)
    );

    setData(filtered);
  };

  useEffect(() => { fetchData(); }, [lastUpdate]);

  // Leyenda solo con series presentes
  const legendPayload = useMemo(() => {
    return SERIES
      .filter((s) => data.some((r) => (r[s.key] ?? 0) > 0))
      .map((s) => ({ id: s.key, type: "square", value: s.key, color: s.color }));
  }, [data]);

  return (
    <div style={{ padding: 16, background: "transparent", borderRadius: 12 }}>
      <h3 style={{ color: "#f5f5f5", marginBottom: 12 }}>📦 Órdenes por Mes</h3>

      <ResponsiveContainer width="100%" height={450}>
        <BarChart
          data={data}
          margin={{ top: 10, right: 20, left: 10, bottom: 36 }}
          barCategoryGap="4%"
          barGap={2}
          maxBarSize={44}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis dataKey="mes" tick={{ fill: "#E5E7EB" }} interval={0} height={32} />
          <YAxis tick={{ fill: "#E5E7EB" }} />
          <Tooltip content={<DarkTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
          <Legend payload={legendPayload} wrapperStyle={{ color: "#E5E7EB" }} />
          {SERIES.map((s) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              fill={s.color}
              barSize={40}
              radius={[6, 6, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default Tabordenes;
